import React, { useEffect, useRef } from 'react'
import { Editor } from '@tinymce/tinymce-react'
import { useTranslation } from 'react-i18next'
import { convertLanguagesToShort } from '../../utils'
import config from '../../config'
import { useSelector } from 'react-redux'

const TinyEditor = ({
                        handleOnEditorKeyUp,
                        handleOnEditorInit,
                        handleOnEditorChange,
                        handleOnMouseOver,
                        handleOnMouseClick,
                        handleOnSelectorChange,
                        editorId,
                        text,
                        shortcutKey,
                        initialValue = '',
                        tabIndex = 0,
                        height = 630,
                        fromAI = false,
                        bordered = true
                    }) => {
    const { i18n: { language: i18nLanguage } } = useTranslation()
    const editorRef = useRef(null)
    const themeState = useSelector(state => state.userData.theme || localStorage.getItem('theme') || config.THEMES_MODES.LIGHT)
    const { skin, contentCss } = config.THEME_CONFIG[themeState] || config.THEME_CONFIG[config.THEMES_MODES.LIGHT]

    const fixStatusbarAccessibility = (editor) => {
        const container = editor?.getContainer()
        if (!container) {
            return
        }
        container.querySelectorAll('.tox-statusbar__path-item').forEach(item => {
            if (item.hasAttribute('aria-level')) {
                item.removeAttribute('aria-level')
            }
            if (item.getAttribute('tabindex') !== '0') {
                item.setAttribute('tabindex', '0')
            }
        })
        const resizeHandle = container.querySelector('.tox-statusbar__resize-handle')
        if (resizeHandle) {
            resizeHandle.setAttribute('aria-hidden', 'true')
            if (resizeHandle.hasAttribute('aria-label')) {
                resizeHandle.removeAttribute('aria-label')
            }
        }
    }

    const handleEditorInit = (event, editor) => {
        if (handleOnEditorInit) {
            handleOnEditorInit(event, editor)
            editorRef.current = editor
        }
        const iframe = editor.iframeElement
        if (iframe) {
            iframe.setAttribute('tabindex', tabIndex.toString())
        }
        if (!bordered) {
            const editorContainer = editor.getContainer()
            if (editorContainer) {
                editorContainer.style.border = 'none'
                editorContainer.style.boxShadow = 'none'
            }
        }
        fixStatusbarAccessibility(editor)
        const handleNodeChange = () => fixStatusbarAccessibility(editor)
        editor.on('NodeChange', handleNodeChange)
        editor.on('Remove', () => {
            editor.off('NodeChange', handleNodeChange)
        })
    }

    const handleEditorKeyDown = (e) => {
        const key = e.key.toLowerCase()
        const accessKeyMap = Object.values(config.accessKeys).map(k => k.toLowerCase())
        if (e.altKey && accessKeyMap.includes(key)) {
            e.preventDefault()
            const targetEl = document.querySelector(`[accesskey="${key.toUpperCase()}"]`)
            if (targetEl) {
                targetEl.focus()
            }
        }
    }
    useEffect(() => {
        const handleAccessKey = (e) => {
            const shortcut = e.key.toLowerCase() === shortcutKey
            if (e.altKey && shortcut) {
                e.preventDefault()
                if (editorRef.current) {
                    editorRef.current.focus()
                }
            }
        }
        window.addEventListener('keydown', handleAccessKey)
        return () => {
            window.removeEventListener('keydown', handleAccessKey)
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    , [])

    return (
        <Editor
            onSelectionChange={(event, editor) => handleOnSelectorChange(event, editor)}
            onEditorChange={(text, editor) => handleOnEditorChange(text, editor)}
            onKeyUp={(key, editor) => handleOnEditorKeyUp(key, editor)}
            onKeyDown={handleEditorKeyDown}
            onInit={handleEditorInit}
            onMouseOver={(event, editor) => handleOnMouseOver(event, editor)}
            onMouseUp={(event, editor) => handleOnMouseClick(event, editor)}
            id={editorId}
            initialValue={initialValue}
            value={text}
            key={themeState}
            init={{
                element_format: 'xhtml',
                promotion: false,
                inline: false,
                language: convertLanguagesToShort(i18nLanguage),
                valid_elements: "*[*]",
                extended_valid_elements: '*[*]',
                cleanup: false,
                verify_html: false,
                branding: false,
                width: '100%',
                plugins: ' preview searchreplace autolink  directionality code visualblocks visualchars fullscreen image link media codesample table charmap pagebreak anchor insertdatetime advlist lists charmap ',
                menubar: '',
                toolbar: fromAI ? '' : ' undo redo | bold italic underline strikethrough | fontfamily fontsize styles | alignleft aligncenter alignright alignjustify  | numlist bullist | forecolor backcolor removeformat | pagebreak charmap  | fullscreen  preview |  link anchor codesample | ltr rtl',
                toolbar_sticky: true,
                image_advtab: true,
                indent: false,
                entity_encoding: 'raw',
                force_br_newlines: false,
                paste_auto_cleanup_on_paste: false,
                remove_linebreaks: false,
                convert_urls: false,
                skin,
                content_css: contentCss,
                link_list: [],
                image_list: [],
                image_class_list: [
                    { title: 'None', value: '' },
                    { title: 'Some class', value: 'class-name' }
                ],
                importcss_append: true,
                height,
                image_caption: true,
                quickbars_selection_toolbar: 'bold italic | quicklink h2 h3 blockquote ',
                noneditable_noneditable_class: 'mceNonEditable',
                toolbar_mode: 'sliding',
                contextmenu: 'link image imagetools table',
            }}
        />
    )
}

export default TinyEditor